import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import './index.scss';
import {
  DashboardOptions,
  CanvasGaugeOptions,
  echartsDegToCanvasRad,
  normalizeSweepDeg,
  degToRad,
  toCanvasGaugeOptions,
} from './utils';
import {
  initNumberTween,
  retargetNumberTween,
  sampleNumberTween,
  type NumberTween,
} from './numberTween';

/**
 * 绘制仪表盘（背景弧 + 进度弧）
 */
function drawGauge(
  canvas: HTMLCanvasElement,
  opts: CanvasGaugeOptions,
  valueOverride?: number
) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  // 1) 使用 CSS 尺寸决定画布逻辑像素大小（取正方形，避免拉伸）。
  const cssW = canvas.clientWidth;
  const cssH = canvas.clientHeight;
  const size = Math.floor(Math.min(cssW, cssH));
  if (size <= 0) return;

  // 2) 按 DPR 缩放实际像素，解决高分屏模糊问题。
  const dpr = window.devicePixelRatio || 1;
  const pxW = Math.floor(size * dpr);
  const pxH = Math.floor(size * dpr);
  if (canvas.width !== pxW) canvas.width = pxW;
  if (canvas.height !== pxH) canvas.height = pxH;

  // 3) 重置 transform，确保不会累积 scale；每次重绘前清屏。
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, size, size);

  // 4) 几何参数：中心点、线宽、半径。
  const cx = size / 2;
  const cy = size / 2;
  const lineWidth = opts.lineWidth || 20;

  // 计算 radius：外圈留出少量 padding，避免边缘裁切
  // 兼容旧实现：size=250, lineWidth=20 时等效 radius=109（外侧约 6px 留白）
  const outerPadding = 6;
  const radius = Math.max(0, size / 2 - outerPadding - lineWidth / 2);

  // 5) 角度系统：复用 ECharts gauge 的 start/end/clockwise 语义，映射到 Canvas rad。
  const startRad = echartsDegToCanvasRad(opts.startAngle);
  const sweepDeg = normalizeSweepDeg(
    opts.startAngle,
    opts.endAngle,
    opts.clockwise
  );
  const sweepRad = degToRad(sweepDeg);
  const bgEndRad = opts.clockwise ? startRad + sweepRad : startRad - sweepRad;

  // 构建或复用渲染缓存：渐变与背景 Path2D 与基于画布尺寸/参数的 key
  const cacheKey = `${size}_${dpr}_${opts.startAngle}_${opts.endAngle}_${opts.lineWidth}_${opts.clockwise}`;
  const anyOpts: any = opts as any;
  let cache = anyOpts._renderCache;
  if (!cache || cache.key !== cacheKey) {
    // 需要在 scale 之后创建 gradient，因为坐标以 CSS 像素为单位
    const bgGrad = ctx.createLinearGradient(cx - radius, cy, cx + radius, cy);
    bgGrad.addColorStop(0, '#0F1E3C');
    bgGrad.addColorStop(1, '#0D306F');

    const progGrad = ctx.createLinearGradient(cx - radius, cy, cx + radius, cy);
    progGrad.addColorStop(0, '#1548A0');
    progGrad.addColorStop(1, '#0C81F7');

    // 预构建背景 Path2D（固定不变），进度弧因 progress 而变，不缓存
    const bgPath = new Path2D();
    bgPath.arc(cx, cy, radius, startRad, bgEndRad, !opts.clockwise);

    cache = {
      key: cacheKey,
      bgGrad,
      progGrad,
      bgPath,
    };
    anyOpts._renderCache = cache;
  }
  // 6) 进度值归一化到 [0,1]（支持 valueOverride：用于平滑动画时覆盖瞬时值）。
  const denom = (opts.max ?? 240) - (opts.min ?? 0);
  const value = typeof valueOverride === 'number' ? valueOverride : opts.value;
  const progress = denom > 0 ? (value - opts.min) / denom : 0;
  const clamped = Math.max(0, Math.min(progress, 1));

  // --- 背景颜色 ---
  // 使用缓存的背景 path 与渐变
  ctx.lineWidth = lineWidth;
  ctx.lineCap = 'butt';
  ctx.strokeStyle = cache.bgGrad;
  ctx.stroke(cache.bgPath);

  // --- 进度条颜色 ---
  if (clamped > 0) {
    // 只绘制有效的进度弧；lineCap=round 让末端更圆润。
    const progressEnd = opts.clockwise
      ? startRad + sweepRad * clamped
      : startRad - sweepRad * clamped;

    // 进度弧使用缓存的渐变，但 arc 依赖 progressEnd，按需绘制
    ctx.beginPath();
    ctx.arc(cx, cy, radius, startRad, progressEnd, !opts.clockwise);
    ctx.strokeStyle = cache.progGrad;
    ctx.lineWidth = lineWidth;
    ctx.lineCap = 'round';
    ctx.stroke();
  }
}

type DashboardState = {
  type: 'speed' | 'angle';
  options?: DashboardOptions;
  currentValue: number | string;
  unit?: string;
  title: string;
};

/**
 * 仪表盘
 *
 * @param {DashboardState} props
 * @return {*}
 */
const Dashboard = (props: DashboardState) => {
  const { type, options, currentValue, unit } = props;

  // canvas DOM 引用：绘制发生在 canvas 上，不依赖 React state。
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // RAF 句柄：作为“锁”防止同一帧重复 schedule
  const rafRef = useRef<number | null>(null);

  // 最新的绘制配置：使用 ref 避免把“绘制数据”放到 state 里引发 re-render。
  const latestOptionsRef = useRef<CanvasGaugeOptions | null>(null);

  // 数值平滑器：只对 value 做 160ms 的过渡（easeOut），减少跳变带来的抖动。
  const valueTweenRef = useRef<NumberTween>({
    from: 0,
    to: 0,
    startTs: 0,
    durationMs: 160,
    active: false,
  });

  // 上次 retarget 的时间与数值，用于合并高频微小变化，避免持续触发动画和重绘。
  const lastRetargetTsRef = useRef<number>(0);
  const lastRetargetValueRef = useRef<number | null>(null);

  // 首帧初始化标记：第一次直接对齐到目标值，不做过渡（避免从 0 飞过去）。
  const hasInitValueRef = useRef(false);

  // 将外部 options统一转换为 CanvasGaugeOptions
  const canvasOptions = useMemo(() => toCanvasGaugeOptions(options), [options]);

  /**
   * RAF tick：
   * - 从 tween 采样当前平滑值
   * - 调用 drawGauge 完成一帧绘制
   * - 若仍在过渡中则继续 requestAnimationFrame
   */
  const tick = useCallback((ts: number) => {
    rafRef.current = null;
    const latest = latestOptionsRef.current;
    const canvas = canvasRef.current;
    if (!latest || !canvas) return;

    const v = sampleNumberTween(valueTweenRef.current, ts);
    drawGauge(canvas, latest, v);

    // 动画过程中持续刷新；否则本帧绘制后停止
    if (valueTweenRef.current.active) {
      rafRef.current = requestAnimationFrame(tick);
    }
  }, []);

  /**
   * 触发一次绘制（合帧）：
   * - 若当前已有 RAF 排队，则不重复排队
   * - 由“数据更新 effect”和“尺寸变化回调”共同调用
   */
  const scheduleDraw = useCallback(() => {
    if (rafRef.current != null) return;
    rafRef.current = requestAnimationFrame(tick);
  }, [tick]);

  // 初始化一次：绘制 & 监听窗口尺寸变化
  useEffect(() => {
    // 初次绘制
    scheduleDraw();

    // 优先使用 ResizeObserver 监听 canvas 尺寸变化；不支持则退化到 window.resize。
    let ro: ResizeObserver | null = null;
    const onWindowResize = () => scheduleDraw();

    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(onWindowResize);
      if (canvasRef.current) ro.observe(canvasRef.current);
    } else {
      window.addEventListener('resize', onWindowResize);
    }

    return () => {
      // 组件卸载：清理 RAF + observer/listener，避免悬挂回调。
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;

      ro?.disconnect();
      ro = null;
      window.removeEventListener('resize', onWindowResize);
    };
  }, [scheduleDraw]);

  // 数据更新：平滑更新进度条
  useEffect(() => {
    // 1) 更新最新 options（绘制会从 ref 读取）。
    latestOptionsRef.current = canvasOptions;
    if (!canvasOptions) {
      hasInitValueRef.current = false;
      return;
    }

    // 2) 仅对 value 做 tween；其他参数（角度/范围/方向等）立即生效。
    const now = performance.now();

    // 为了避免上层高频且精细的数值（如 frontAngle 的小数位）造成持续重绘，
    // 在绘制层对 value 做轻量的量化与合并：
    // - 对数值进行 quantize（步长按类型选取），减少微小浮动；
    // - 对 retarget 设置最小间隔，合并短时间内的多次变更。
    const rawTarget = canvasOptions.value;
    const quantStep =
      typeof canvasOptions.displayQuantStep === 'number'
        ? canvasOptions.displayQuantStep
        : type === 'angle'
          ? 0.1
          : 0.1; // 向后兼容默认
    const quantTarget = Math.round(rawTarget / quantStep) * quantStep;

    const lastVal = lastRetargetValueRef.current;
    const timeSince = now - (lastRetargetTsRef.current || 0);
    const minInterval =
      typeof canvasOptions.minRetargetMs === 'number'
        ? canvasOptions.minRetargetMs
        : 80; // ms, 默认最小重定向间隔

    if (!hasInitValueRef.current) {
      // 首次直接对齐到目标值（使用量化后的目标值，不修改上层原始值）
      initNumberTween(valueTweenRef.current, quantTarget, now);
      hasInitValueRef.current = true;
      lastRetargetValueRef.current = quantTarget;
      lastRetargetTsRef.current = now;
    } else {
      // 当变化显著（>= 半个步长）或距离上次 retarget 超过最小间隔时，才重新 target。
      const significantChange =
        lastVal === null
          ? true
          : Math.abs(quantTarget - lastVal) >= quantStep / 2;
      if (significantChange || timeSince > minInterval) {
        retargetNumberTween(valueTweenRef.current, quantTarget, now, 160);
        lastRetargetValueRef.current = quantTarget;
        lastRetargetTsRef.current = now;
      }
    }

    // 3) 触发绘制：如果 tween 激活，tick 会在动画期间持续刷新。
    scheduleDraw();
  }, [canvasOptions]);

  return (
    <div className={`dashboard dashboard-${type}`}>
      <canvas ref={canvasRef} className="dashboard-canvas" />
      <div className={`dashboard-center dashboard-center-${type}`}>
        <i className="dashboard-label">
          {props.title || (type === 'angle' ? '角度' : '速度')}
        </i>
        <i className="dashboard-value">
          {currentValue}
          {type === 'angle' && '°'}
        </i>
        {unit && <i className="dashboard-unit">{unit}</i>}
      </div>
    </div>
  );
};

export default React.memo(Dashboard);
